import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash, ClipboardCheck, AlertCircle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { QaCriteriaDialog } from "../components/QaCriteriaDialog";
import { qaCriteriaApi, settingsQueryKeys } from "../api";
import type { QaCriteriaType } from "../types";
import { useAuth } from "@/features/auth";

type ReviewStage =
  | "LEAD_PENDING"
  | "PARTNER_PENDING"
  | "BILLING_PENDING"
  | "MATTER";

// Maps UI stage tabs to backend QA criteria type values.
// Note: the PENDING_* stages are legacy (per-task QA was removed in CR-003);
// MATTER criteria are the ones now used, for matter-close QA scoring (CR-004).
const STAGE_TO_TYPE: Record<ReviewStage, QaCriteriaType> = {
  LEAD_PENDING: "PENDING_LEAD",
  PARTNER_PENDING: "PENDING_PARTNER",
  BILLING_PENDING: "PENDING_BILLING",
  MATTER: "MATTER",
};

const STAGES: {
  value: ReviewStage;
  labelKey: string;
  color: string;
  activeColor: string;
}[] = [
  {
    value: "LEAD_PENDING",
    labelKey: "newTaskStatus.LEAD_PENDING",
    color: "bg-blue-400",
    activeColor: "bg-blue-500 text-white",
  },
  {
    value: "PARTNER_PENDING",
    labelKey: "newTaskStatus.PARTNER_PENDING",
    color: "bg-indigo-400",
    activeColor: "bg-indigo-500 text-white",
  },
  {
    value: "BILLING_PENDING",
    labelKey: "newTaskStatus.BILLING_PENDING",
    color: "bg-amber-400",
    activeColor: "bg-amber-500 text-white",
  },
  {
    value: "MATTER",
    labelKey: "qa.matterStage",
    color: "bg-emerald-400",
    activeColor: "bg-emerald-500 text-white",
  },
];

const WEIGHT_BAR_COLORS = [
  "bg-blue-500",
  "bg-indigo-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-purple-500",
  "bg-rose-500",
  "bg-cyan-500",
  "bg-orange-500",
];

export function QaCriteriaPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user, hasAccess } = useAuth();

  const canCreate = hasAccess("qa-criteria", "create");
  const canUpdate = hasAccess("qa-criteria", "update");
  const canDelete = hasAccess("qa-criteria", "delete");
  const canModify = canUpdate || canDelete;

  // MD Partner and Manager see all stages; Lead/Partner only see their own.
  const roleType = user?.role?.type;
  const showAllStages = roleType === "MD" || roleType === "MANAGER";

  const visibleStages = showAllStages
    ? STAGES
    : roleType === "LEAD"
      ? STAGES.filter((s) => s.value === "LEAD_PENDING")
      : roleType === "PARTNER"
        ? STAGES.filter((s) => s.value === "PARTNER_PENDING")
        : STAGES;

  const defaultStage: ReviewStage = visibleStages[0]?.value ?? "LEAD_PENDING";
  const [activeStage, setActiveStage] = useState<ReviewStage>(defaultStage);

  const activeType = STAGE_TO_TYPE[activeStage];

  // Fetch all QA criteria
  const { data, isLoading } = useQuery({
    queryKey: settingsQueryKeys.qaCriteria.all,
    queryFn: () => qaCriteriaApi.getAll({ page: 0, limit: 1000 }),
  });

  // Filter criteria by active stage type
  const criteria = (data?.results ?? []).filter((c) => c.type === activeType);
  const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0);
  const isWeightValid = totalWeight === 100;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: qaCriteriaApi.create,
    onSuccess: () => {
      toast.success(t('qa.criterionCreated') || 'Criterion created');
      void queryClient.invalidateQueries({ queryKey: settingsQueryKeys.qaCriteria.all });
    },
    onError: (err: Error) => {
      toast.error(err.message || t('creatingFailed'));
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof qaCriteriaApi.update>[1] }) =>
      qaCriteriaApi.update(id, data),
    onSuccess: () => {
      toast.success(t('qa.criterionUpdated') || 'Criterion updated');
      void queryClient.invalidateQueries({ queryKey: settingsQueryKeys.qaCriteria.all });
    },
    onError: (err: Error) => {
      toast.error(err.message || t('updatingError'));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => qaCriteriaApi.delete(id),
    onSuccess: () => {
      toast.success(t('qa.criterionDeleted') || 'Criterion deleted');
      void queryClient.invalidateQueries({ queryKey: settingsQueryKeys.qaCriteria.all });
    },
    onError: (err: Error) => {
      toast.error(err.message || t('deletingFailed'));
    },
  });

  const handleSave = (data: { id?: number; name: string; description: string; weight: number; type: QaCriteriaType }) => {
    if (data.id) {
      updateMutation.mutate({ id: data.id, data: { name: data.name, description: data.description, weight: data.weight, type: data.type } });
    } else {
      createMutation.mutate({ name: data.name, description: data.description, weight: data.weight, type: data.type });
    }
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5" />
          {t("qa.criteriaManagement")}
        </h1>
        <div className="flex items-center gap-3">
          <Link to="/app/settings">
            <Button variant="outline">{t("settings")}</Button>
          </Link>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Stage Tabs — only shown to MD Partner and Manager */}
        <div
          className={cn(
            "flex flex-wrap items-center gap-1 rounded-lg bg-slate-100 p-1",
            !showAllStages && "hidden",
          )}
        >
          {visibleStages.map((stage) => {
            const isActive = activeStage === stage.value;
            return (
              <button
                key={stage.value}
                type="button"
                onClick={() => setActiveStage(stage.value)}
                className={cn(
                  "relative flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all",
                  isActive
                    ? cn(stage.activeColor, "shadow-sm")
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/70",
                )}
              >
                <span
                  className={cn(
                    "h-2 w-2 rounded-full",
                    isActive ? "bg-white" : stage.color,
                  )}
                />

                {t(stage.labelKey)}
              </button>
            );
          })}
        </div>

        {/* Weight Summary Card */}
        <Card>
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                {t("qa.weightDistribution")}
              </CardTitle>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {t("qa.totalWeight")}:
                  </span>
                  <Badge
                    className={cn(
                      "text-sm font-semibold",
                      isWeightValid
                        ? "bg-green-100 text-green-700 hover:bg-green-100"
                        : "bg-red-100 text-red-700 hover:bg-red-100",
                    )}
                  >
                    {totalWeight}%
                  </Badge>
                  {!isWeightValid && (
                    <span className="flex items-center gap-1 text-xs text-red-500">
                      <AlertCircle className="h-3 w-3" />
                      {totalWeight < 100
                        ? t("qa.weightUnder")
                        : t("qa.weightOver")}
                    </span>
                  )}
                </div>
                {canCreate && (
                  <QaCriteriaDialog
                    currentTotalWeight={totalWeight}
                    type={activeType}
                    onSave={handleSave}
                  />
                )}
              </div>
            </div>
          </CardHeader>

          {/* Weight Bar */}
          <div className="px-4 pb-3">
            <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden flex">
              {criteria.map((c, i) => (
                <div
                  key={c.id}
                  className={cn(
                    "h-full transition-all",
                    WEIGHT_BAR_COLORS[i % WEIGHT_BAR_COLORS.length],
                  )}
                  style={{ width: `${c.weight}%` }}
                  title={`${c.name}: ${c.weight}%`}
                />
              ))}
            </div>
            <div className="flex mt-1.5 gap-3 flex-wrap">
              {criteria.map((c, i) => (
                <div
                  key={c.id}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                  <span
                    className={cn(
                      "h-2 w-2 rounded-full",
                      WEIGHT_BAR_COLORS[i % WEIGHT_BAR_COLORS.length],
                    )}
                  />
                  {c.name} ({c.weight}%)
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Criteria Table */}
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>{t("name")}</TableHead>
                <TableHead>{t("description")}</TableHead>
                <TableHead className="w-28 text-center">
                  {t("qa.weight")}
                </TableHead>
                {canModify && (
                  <TableHead className="w-24">{t("actionsLabel")}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={canModify ? 5 : 4} className="h-24 text-center">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : criteria.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={canModify ? 5 : 4}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {t("qa.noCriteria")}
                  </TableCell>
                </TableRow>
              ) : (
                criteria.map((c, index) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {c.description}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="font-mono">
                        {c.weight}%
                      </Badge>
                    </TableCell>
                    {canModify && (
                      <TableCell>
                        <div className="flex gap-0">
                          {canUpdate && (
                            <QaCriteriaDialog
                              criterion={c}
                              currentTotalWeight={totalWeight}
                              type={activeType}
                              onSave={handleSave}
                            />
                          )}
                          {canDelete && (
                            <Button
                              size="sm"
                              variant="outline"
                              className={cn(
                                "text-red-600 hover:text-red-700 hover:bg-red-50",
                                canUpdate ? "rounded-l-none" : "",
                              )}
                              onClick={() => handleDelete(c.id)}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
